import {
	CardTypeEnum,
	PaymentCardStateEnum,
	PaymentCardStateReasonEnum,
	WalletEnum,
} from '.spec/self-care/tsTypes';
import { endOfMonth, format, isSameMonth } from 'date-fns';
import { take } from 'lodash';

import type { Amount } from '@skippay/core/@types/amount';
import type { CoinsOverview } from '@skippay/core/@types/coinsOverview';
import { toFloat } from '@skippay/core/utils/amountUtils';
import { Paginated } from '@skippay/core/utils/queryUtils';
import { Maybe } from '@skippay/core/utils/types';

import { toIsoDate } from 'jarabot/utils';
import CoinsOverviewQuery from 'queries/CoinsOverviewQuery.graphql';

import { LIST_CAPS } from '../../constants';
import JarabotCardsQuery from '../../queries/JarabotCardsQuery.graphql';
import JarabotCashbackQuery from '../../queries/JarabotCashbackQuery.graphql';
import { CardInfo, CashbackInfo, CoinsInfo } from '../../types';
import { DomainSource } from '../types';

interface CoinsData {
	coinsOverview?: Maybe<
		Partial<Pick<CoinsOverview, 'coinBalance'>> & {
			earliestExpiration?: Maybe<Partial<CoinsOverview['earliestExpiration']>>;
		}
	>;
}

export const coinsSource: DomainSource<CoinsData> = {
	domains: ['coins'],
	query: CoinsOverviewQuery,
	toModel: (data) => {
		const overview = data.coinsOverview;
		if (!overview) return { coins: undefined };
		const expiration = overview.earliestExpiration;
		const coins: CoinsInfo = {
			balance: overview.coinBalance ? toFloat(overview.coinBalance) : undefined,
			nearestExpiration: expiration
				? {
						date: toIsoDate(expiration.date),
						amount: expiration.sum ? toFloat(expiration.sum) : undefined,
					}
				: undefined,
		};
		return { coins };
	},
};

interface CashbackWalletRow {
	availableCashback?: Maybe<Amount>;
	endOfMonthExpirations?: Maybe<Amount>;
	pendingCashback?: Maybe<Amount>;
	totalAppliedCashback?: Maybe<Amount>;
	totalBonusCashback?: Maybe<Amount>;
}

export const cashbackSource: DomainSource<{
	myCashbackWallet?: CashbackWalletRow | null;
}> = {
	domains: ['cashback'],
	query: JarabotCashbackQuery,
	toModel: (data) => {
		const wallet = data.myCashbackWallet;
		if (!wallet) return { cashback: undefined };
		const expiring = wallet.endOfMonthExpirations ? toFloat(wallet.endOfMonthExpirations) : 0;
		const cashback: CashbackInfo = {
			available: wallet.availableCashback ? toFloat(wallet.availableCashback) : undefined,
			pending: wallet.pendingCashback ? toFloat(wallet.pendingCashback) : undefined,
			totalApplied: wallet.totalAppliedCashback ? toFloat(wallet.totalAppliedCashback) : undefined,
			totalBonus: wallet.totalBonusCashback ? toFloat(wallet.totalBonusCashback) : undefined,
			nearestExpiration:
				expiring > 0
					? { date: format(endOfMonth(new Date()), 'yyyy-MM-dd'), amount: expiring }
					: undefined,
		};
		return { cashback };
	},
};

interface CardRow {
	atmWithdrawalEnabled?: Maybe<boolean>;
	canApplyForNewCardIssuance?: Maybe<boolean>;
	canShowPin?: Maybe<boolean>;
	cardType?: Maybe<CardTypeEnum>;
	expirationDate?: Maybe<string>;
	onlinePaymentsEnabled?: Maybe<boolean>;
	state?: Maybe<PaymentCardStateEnum>;
	stateReason?: Maybe<PaymentCardStateReasonEnum>;
	validThru?: Maybe<string>;
	wallets?: Maybe<WalletEnum[]>;
}

const isExpiringSoon = (expirationDate?: Maybe<string>): boolean | undefined => {
	if (!expirationDate) return undefined;
	const expiration = new Date(expirationDate);
	return Number.isNaN(expiration.getTime()) ? undefined : isSameMonth(expiration, new Date());
};

const toCardInfo = (card: CardRow): CardInfo => ({
	state: card.state ?? undefined,
	stateReason: card.stateReason ?? undefined,
	cardType: card.cardType ?? undefined,
	wallets: card.wallets ?? undefined,
	canShowPin: card.canShowPin ?? undefined,
	onlinePaymentsEnabled: card.onlinePaymentsEnabled ?? undefined,
	atmWithdrawalEnabled: card.atmWithdrawalEnabled ?? undefined,
	canApplyForNewCardIssuance: card.canApplyForNewCardIssuance ?? undefined,
	validThru: card.validThru ?? undefined,
	isExpiringSoon: isExpiringSoon(card.expirationDate),
});

export const cardSource: DomainSource<{ myContractCards?: Paginated<CardRow> }> = {
	domains: ['card'],
	query: JarabotCardsQuery,
	toModel: (data) => {
		const cardInfos = take(data.myContractCards?.data, LIST_CAPS.cards).map(toCardInfo);
		const card = cardInfos.find((info) => info.state === PaymentCardStateEnum.ACTIVATED);
		return {
			card: card ?? cardInfos[0],
			cards: cardInfos.length ? cardInfos : undefined,
		};
	},
};
